import type { Result } from 'true-myth';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode } from '~/shared/internationalization';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    AgentAttributionsResponse,
    AgentLibraryFiltersResponse,
    AgentLibraryPositionsResponse,
    AgentLibraryResponse,
    RecommendedAgentsResponse,
} from '../../data-contract/recommended-agents';
import {
    agentAttributionsResponseSchema,
    agentLibraryFiltersResponseSchema,
    agentLibraryPositionsResponseSchema,
    agentLibraryResponseSchema,
    recommendedAgentsResponseSchema,
} from '../../data-contract/recommended-agents';

type RequestContext = {
    readonly acceptLanguage?: LanguageCode;
    readonly signal?: AbortSignal;
};

export type FetchItemRecommendedAgentsInput = RequestContext & {
    readonly architectureId: string;
    readonly itemId: string;
};

export type FetchPositionRecommendedAgentsInput = RequestContext & {
    readonly architectureId: string;
    readonly positionId: string;
};

/** The architecture an agents library reads: a job architecture, or a position architecture with its positions. */
export type AgentLibraryScope = {
    readonly architectureId: string;
    readonly kind: 'job' | 'position';
};

/** The library's filters; the job role, job level and position ids each match an agent recommended for any of them. */
export type AgentLibraryFilters = {
    readonly autonomyLevels: readonly number[];
    readonly businessFunctions: readonly string[];
    readonly levelIds: readonly string[];
    readonly positionIds: readonly string[];
    readonly roleIds: readonly string[];
    readonly search: string;
};

/** One page of the library: the cursor of the previous page's `nextCursor`, none for the first page. */
export type AgentLibraryPage = {
    readonly cursor: string | undefined;
    readonly limit: number;
};

export type FetchAgentLibraryInput = RequestContext & {
    readonly filters: AgentLibraryFilters;
    readonly page: AgentLibraryPage;
    readonly scope: AgentLibraryScope;
};

export type FetchAgentLibraryFiltersInput = RequestContext & {
    readonly scope: AgentLibraryScope;
};

export type FetchAgentLibraryPositionsInput = RequestContext & {
    readonly architectureId: string;
};

export type FetchAgentAttributionsInput = RequestContext & {
    readonly agentId: string;
    readonly scope: AgentLibraryScope;
};

export type RecommendedAgentsFetchers = {
    readonly fetchAgentAttributions: (
        input: FetchAgentAttributionsInput,
    ) => Promise<Result<AgentAttributionsResponse, HttpFetcherError>>;
    readonly fetchAgentLibrary: (
        input: FetchAgentLibraryInput,
    ) => Promise<Result<AgentLibraryResponse, HttpFetcherError>>;
    readonly fetchAgentLibraryFilters: (
        input: FetchAgentLibraryFiltersInput,
    ) => Promise<Result<AgentLibraryFiltersResponse, HttpFetcherError>>;
    readonly fetchAgentLibraryPositions: (
        input: FetchAgentLibraryPositionsInput,
    ) => Promise<Result<AgentLibraryPositionsResponse, HttpFetcherError>>;
    readonly fetchItemRecommendedAgents: (
        input: FetchItemRecommendedAgentsInput,
    ) => Promise<Result<RecommendedAgentsResponse, HttpFetcherError>>;
    readonly fetchPositionRecommendedAgents: (
        input: FetchPositionRecommendedAgentsInput,
    ) => Promise<Result<RecommendedAgentsResponse, HttpFetcherError>>;
};

export type RecommendedAgentsFetchersDependencies = {
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

const requestOptions = ({ acceptLanguage, signal }: RequestContext) => ({
    headers: acceptLanguage === undefined ? undefined : { 'Accept-Language': acceptLanguage },
    signal,
});

const agentsPathOf = ({ architectureId, kind }: AgentLibraryScope): `/${string}` =>
    kind === 'job'
        ? `/job-architectures/${encodeURIComponent(architectureId)}/agents`
        : `/ai-transformation/architectures/${encodeURIComponent(architectureId)}/agents`;

/**
 * The library's query: each business function, autonomy level, job role, job level and position repeated, the search
 * only when it holds text, and the page's size with its cursor after the first page.
 */
export const toAgentLibrarySearchParams = (
    { autonomyLevels, businessFunctions, levelIds, positionIds, roleIds, search }: AgentLibraryFilters,
    { cursor, limit }: AgentLibraryPage,
): URLSearchParams => {
    const params = new URLSearchParams();
    for (const businessFunction of businessFunctions) params.append('businessFunction', businessFunction);
    for (const autonomyLevel of autonomyLevels) params.append('autonomyLevel', String(autonomyLevel));
    for (const roleId of roleIds) params.append('roleId', roleId);
    for (const levelId of levelIds) params.append('levelId', levelId);
    for (const positionId of positionIds) params.append('positionId', positionId);
    const query = search.trim();
    if (query !== '') params.set('q', query);
    if (cursor !== undefined) params.set('cursor', cursor);
    params.set('limit', String(limit));

    return params;
};

export const createRecommendedAgentsFetchers = ({
    validatingCobrainerApiFetcher,
}: RecommendedAgentsFetchersDependencies): RecommendedAgentsFetchers => ({
    fetchAgentAttributions: ({ agentId, scope, ...context }) =>
        validatingCobrainerApiFetcher.get(agentAttributionsResponseSchema, {
            pathname: `${agentsPathOf(scope)}/${encodeURIComponent(agentId)}`,
            requestOptions: requestOptions(context),
        }),
    fetchAgentLibrary: ({ filters, page, scope, ...context }) =>
        validatingCobrainerApiFetcher.get(agentLibraryResponseSchema, {
            pathname: agentsPathOf(scope),
            requestOptions: { ...requestOptions(context), searchParams: toAgentLibrarySearchParams(filters, page) },
        }),
    fetchAgentLibraryFilters: ({ scope, ...context }) =>
        validatingCobrainerApiFetcher.get(agentLibraryFiltersResponseSchema, {
            pathname: `${agentsPathOf(scope)}/filters`,
            requestOptions: requestOptions(context),
        }),
    fetchAgentLibraryPositions: ({ architectureId, ...context }) =>
        validatingCobrainerApiFetcher.get(agentLibraryPositionsResponseSchema, {
            pathname: `/job-architectures/${encodeURIComponent(architectureId)}/agents/positions`,
            requestOptions: requestOptions(context),
        }),
    fetchItemRecommendedAgents: ({ architectureId, itemId, ...context }) =>
        validatingCobrainerApiFetcher.get(recommendedAgentsResponseSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/agents`,
            requestOptions: requestOptions(context),
        }),
    fetchPositionRecommendedAgents: ({ architectureId, positionId, ...context }) =>
        validatingCobrainerApiFetcher.get(recommendedAgentsResponseSchema, {
            pathname: `/ai-transformation/architectures/${architectureId}/positions/${positionId}/agents`,
            requestOptions: requestOptions(context),
        }),
});
